import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { AiPreview } from '@/mocks/dashboard';
import { getFinishingPreviewList } from '@/lib/finishingPreviewListService';
import DashboardAiPreviews from './components/DashboardAiPreviews';

export default function AiPreviewsPage() {
  const { t } = useTranslation('dashboard');
  const [previews, setPreviews] = useState<AiPreview[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadPreviews = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getFinishingPreviewList();
      setPreviews(data);
    } catch (err) {
      console.error('[AiPreviewsPage] Failed to load previews:', err);
      setError(err instanceof Error ? err.message : 'Failed to load previews');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPreviews();
  }, [loadPreviews]);

  return (
    <div>
      <div className="mb-6 flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-ink-700">{t('dashboard.aiPreviews.title')}</h1>
          <p className="text-sm text-ink-300 mt-1">AI-generated finish previews</p>
        </div>
        <a
          href="/packaging"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-warm-500 text-white text-sm font-bold hover:bg-warm-600 transition-colors whitespace-nowrap"
        >
          <i className="ri-sparkling-2-line" />
          {t('dashboard.aiPreviews.requestQuote', { defaultValue: 'Request Quote' })}
        </a>
      </div>

      {loading && (
        <div className="rounded-xl border border-ink-100 bg-white py-16 flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-[3px] border-warm-200 border-t-warm-500 rounded-full animate-spin" />
          <p className="text-sm text-ink-400">후가공 이미지를 불러오는 중...</p>
        </div>
      )}

      {!loading && error && (
        <div className="rounded-xl border border-ink-100 bg-white py-16 flex flex-col items-center gap-3 text-center px-4">
          <div className="w-14 h-14 rounded-full bg-rose-100 flex items-center justify-center">
            <i className="ri-error-warning-line text-rose-500 text-2xl" />
          </div>
          <p className="text-sm text-rose-600 max-w-md">{error}</p>
          <button
            onClick={loadPreviews}
            className="px-4 py-2 text-sm font-bold text-warm-600 border border-warm-300 rounded-lg hover:bg-warm-50 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-refresh-line mr-1" />
            다시 시도
          </button>
        </div>
      )}

      {!loading && !error && <DashboardAiPreviews previews={previews} />}
    </div>
  );
}